import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, Upload, X, FileText, XCircle } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { RequiredFieldIndicator, FormLegend } from "@/components/RequiredFieldIndicator";
import { AdminLogger } from "@/lib/adminLogger";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
const formSchema = z.object({
  requestType: z.string().min(1, "Please select a request type"),
  adjusterFirstName: z.string().optional(),
  adjusterLastName: z.string().optional(),
  company: z.string().optional(),
  phone: z.string().optional(),
  ext: z.string().optional(),
  adjusterEmail: z.string().optional(),
  insuranceCarrierName: z.string().min(1, "Insurance carrier name is required"),
  claimNumber: z.string().optional(),
  dateOfLoss: z.string().optional(),
  aleLimit: z.string().optional(),
  aleAdvanced: z.string().optional(),
  policyTimeLimit: z.string().optional(),
  policyType: z.string().optional(),
  typeOfLoss: z.string().optional(),
  otherExplanation: z.string().optional(),
  policyholderFirstName: z.string().min(1, "First name is required"),
  policyholderLastName: z.string().min(1, "Last name is required"),
  cellPhone: z.string().optional(),
  policyholderEmail: z.string().optional(),
  additionalContactFirstName: z.string().optional(),
  additionalContactLastName: z.string().optional(),
  additionalContactCellPhone: z.string().optional(),
  additionalContactEmail: z.string().optional(),
  streetAddress: z.string().min(1, "Street address is required"),
  unit: z.string().optional(),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  zip: z.string().min(1, "Zip code is required"),
  squareFootage: z.string().optional(),
  bedrooms: z.string().optional(),
  bathrooms: z.string().optional(),
  numAdults: z.string().optional(),
  numKids: z.string().optional(),
  pets: z.string().optional(),
  numPets: z.string().optional(),
  petDescription: z.string().optional(),
  unknownBudget: z.boolean().optional(),
  leaseLength: z.string().optional(),
  hotelApprovedTo: z.date().optional(),
  hotelRooms: z.string().optional(),
  specialCircumstances: z.string().optional(),
  deductibleRequired: z.string().optional(),
  deductibleAmount: z.string().optional(),
  frvRequested: z.string().optional()
}).refine(data => {
  // If FRV Only, no additional validation needed
  if (data.requestType === "frv-only") {
    return true;
  }

  // For non-FRV requests, validate all required fields
  const requiredFields = [data.adjusterFirstName, data.adjusterLastName, data.company, data.phone, data.adjusterEmail, data.claimNumber, data.dateOfLoss, data.policyType, data.typeOfLoss, data.cellPhone, data.numAdults, data.numKids, data.pets];
  const allRequiredFieldsFilled = requiredFields.every(field => field && field.length > 0);
  if (!allRequiredFieldsFilled) {
    return false;
  }

  // If request type is "hotel-only", require hotelApprovedTo
  if (data.requestType === "hotel-only") {
    return data.hotelApprovedTo !== undefined;
  }

  // For housing request, require leaseLength
  return data.leaseLength && data.leaseLength.length > 0;
}, {
  message: "Please fill in all required fields for your request type",
  path: ["requestType"]
});
const Claims = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [claimId, setClaimId] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [currentUserName, setCurrentUserName] = useState<string>("");
  const [closeReason, setCloseReason] = useState<string>("");
  const [closingClaim, setClosingClaim] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);

  const handleCloseClaim = async () => {
    if (!claimId || !user) return;
    setClosingClaim(true);
    try {
      const { error } = await supabase.functions.invoke("notify-close-claim", {
        body: { claimId, reason: closeReason.trim() || undefined },
      });
      if (error) throw error;
      toast.success("Close request sent to TPS Housing");
      setCloseDialogOpen(false);
      setCloseReason("");
      navigate("/dashboard");
    } catch (err: any) {
      console.error("Close claim error:", err);
      toast.error("Failed to send close request");
    } finally {
      setClosingClaim(false);
    }
  };
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setUploadedFiles(prev => [...prev, ...newFiles]);
    }
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      const newFiles = Array.from(e.dataTransfer.files).filter(file => /\.(pdf|doc|docx|jpg|jpeg|png)$/i.test(file.name));
      setUploadedFiles(prev => [...prev, ...newFiles]);
    }
  };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };
  useEffect(() => {
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
      if (!session?.user) {
        navigate("/auth");
      }
    });
    supabase.auth.getSession().then(({
      data: {
        session
      }
    }) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
      if (!session?.user) {
        navigate("/auth");
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate]);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      requestType: "",
      adjusterFirstName: "",
      adjusterLastName: "",
      company: "",
      phone: "",
      ext: "",
      adjusterEmail: "",
      insuranceCarrierName: "",
      claimNumber: "",
      dateOfLoss: "",
      aleLimit: "",
      aleAdvanced: "",
      policyTimeLimit: "",
      policyType: "",
      typeOfLoss: "",
      otherExplanation: "",
      policyholderFirstName: "",
      policyholderLastName: "",
      cellPhone: "",
      policyholderEmail: "",
      additionalContactFirstName: "",
      additionalContactLastName: "",
      additionalContactCellPhone: "",
      additionalContactEmail: "",
      streetAddress: "",
      unit: "",
      city: "",
      state: "",
      zip: "",
      squareFootage: "",
      bedrooms: "",
      bathrooms: "",
      numAdults: "",
      numKids: "",
      pets: "",
      numPets: "",
      petDescription: "",
      unknownBudget: false,
      leaseLength: "",
      hotelApprovedTo: undefined,
      hotelRooms: "",
      specialCircumstances: "",
      deductibleRequired: "",
      deductibleAmount: "",
      frvRequested: ""
    }
  });

  // Fetch and autofill adjuster profile data from Account Profile
  useEffect(() => {
    const fetchAdjusterProfile = async () => {
      if (!user) return;

      // Fetch profile data
      const {
        data: profileData
      } = await supabase.from("profiles").select("first_name, last_name, company, contact_phone, contact_email").eq("id", user.id).maybeSingle();

      // Autofill from profile
      if (profileData) {
        // Set the current user's name for submitted_by tracking
        const fullName = [profileData.first_name, profileData.last_name].filter(Boolean).join(" ");
        setCurrentUserName(fullName || user.email || "Unknown");

        if (profileData.first_name) {
          form.setValue("adjusterFirstName", profileData.first_name);
        }
        if (profileData.last_name) {
          form.setValue("adjusterLastName", profileData.last_name);
        }
        if (profileData.company) {
          form.setValue("company", profileData.company);
        }
        if (profileData.contact_phone) {
          form.setValue("phone", profileData.contact_phone);
        }
        if (profileData.contact_email) {
          form.setValue("adjusterEmail", profileData.contact_email);
        }
      } else {
        // Fallback to email if no profile
        setCurrentUserName(user.email || "Unknown");
      }

      // Fallback to user email if not in profile
      if (!profileData?.contact_email && user.email) {
        form.setValue("adjusterEmail", user.email);
      }
    };
    fetchAdjusterProfile();
  }, [user, form]);

  // Load claim data if editing
  useEffect(() => {
    const loadClaimData = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const stateClaimId = location.state?.claimId || urlParams.get("edit");
      if (!stateClaimId) return;
      setEditMode(true);
      setClaimId(stateClaimId);
      try {
        const {
          data: claim,
          error
        } = await supabase.from("claims").select("*").eq("id", stateClaimId).single();
        if (error) throw error;

        // Parse adjuster name
        const [adjusterFirstName, ...adjusterLastNameParts] = claim.adjuster_name.split(" ");
        const adjusterLastName = adjusterLastNameParts.join(" ");

        // Parse policyholder name
        const [policyholderFirstName, ...policyholderLastNameParts] = claim.policyholder_name.split(" ");
        const policyholderLastName = policyholderLastNameParts.join(" ");

        // Parse address
        const addressParts = claim.policyholder_address.match(/^(.+?)(?:\s+Unit\s+(.+))?$/);
        const streetAddress = addressParts?.[1] || claim.policyholder_address;
        const unit = addressParts?.[2] || "";

        // Parse family_dynamics
        const familyDynamics = claim.family_dynamics || "";
        const adultsMatch = familyDynamics.match(/Adults:\s*(\d+)/);
        const childrenMatch = familyDynamics.match(/Children:\s*(\d+)/);
        const petsMatch = familyDynamics.match(/Pets:\s*(\w+)/);
        const sqftMatch = familyDynamics.match(/(\d+)\s*sqft/);
        const bedMatch = familyDynamics.match(/(\d+)\s*bed/);
        const bathMatch = familyDynamics.match(/(\d+(?:\.\d+)?)\s*bath/);
        const secondaryContactMatch = familyDynamics.match(/Secondary Contact:\s*([^\d]+?)\s+([\d\s]+)\s+([^\s]+@[^\s]+)/);

        // Parse approved_terms
        const approvedTerms = claim.approved_terms || "";
        const leaseLengthMatch = approvedTerms.match(/Lease:\s*([^\,]+)/);
        const hotelApprovedMatch = approvedTerms.match(/Hotel Approved To:\s*([^\,]+)/);
        const hotelRoomsMatch = approvedTerms.match(/Hotel Rooms:\s*(\d+)/);
        const policyTimeLimitMatch = approvedTerms.match(/Policy Time Limit:\s*([^\,]+)/);
        const aleAdvancedMatch = approvedTerms.match(/ALE Advanced:\s*([\d.]+)/);
        const deductibleRequiredMatch = approvedTerms.match(/Deductible Required:\s*(\w+)/);
        const deductibleAmountMatch = approvedTerms.match(/Deductible Amount:\s*\$?([\d.]+)/);

        // Determine request type based on data
        let requestType = "housing";
        if (leaseLengthMatch) {
          requestType = "housing";
        } else if (hotelApprovedMatch) {
          requestType = "hotel-only";
        }

        // Populate form
        form.reset({
          requestType,
          adjusterFirstName,
          adjusterLastName,
          company: claim.adjuster_company,
          phone: claim.adjuster_phone,
          ext: claim.adjuster_license,
          adjusterEmail: claim.adjuster_email,
          claimNumber: claim.claim_number,
          dateOfLoss: claim.loss_date,
          aleLimit: claim.settlement_amount?.toString() || "",
          aleAdvanced: aleAdvancedMatch?.[1] || "",
          policyTimeLimit: policyTimeLimitMatch?.[1] || "",
          policyType: "",
          typeOfLoss: claim.claim_type,
          otherExplanation: "",
          policyholderFirstName,
          policyholderLastName,
          cellPhone: claim.policyholder_phone,
          policyholderEmail: claim.policyholder_email,
          additionalContactFirstName: secondaryContactMatch ? secondaryContactMatch[1].trim() : "",
          additionalContactLastName: "",
          additionalContactCellPhone: secondaryContactMatch ? secondaryContactMatch[2].replace(/\s/g, "") : "",
          additionalContactEmail: secondaryContactMatch?.[3] || "",
          streetAddress,
          unit,
          city: claim.policyholder_city,
          state: claim.policyholder_state,
          zip: claim.policyholder_zip,
          squareFootage: sqftMatch?.[1] || "",
          bedrooms: bedMatch?.[1] || "",
          bathrooms: bathMatch?.[1] || "",
          numAdults: adultsMatch?.[1] || "",
          numKids: childrenMatch?.[1] || "",
          pets: petsMatch?.[1] || "",
          numPets: "",
          petDescription: "",
          unknownBudget: false,
          leaseLength: leaseLengthMatch?.[1].trim() || "",
          hotelApprovedTo: hotelApprovedMatch ? (() => {
            const date = new Date(hotelApprovedMatch[1]);
            return isNaN(date.getTime()) ? undefined : date;
          })() : undefined,
          hotelRooms: hotelRoomsMatch?.[1] || "",
          specialCircumstances: "",
          deductibleRequired: deductibleRequiredMatch?.[1] || "",
          deductibleAmount: deductibleAmountMatch?.[1] || ""
        });
        toast.success("Claim loaded for editing");
      } catch (error: any) {
        toast.error("Failed to load claim data");
        console.error("Error loading claim:", error);
      }
    };
    loadClaimData();
  }, [location.state, form]);
  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!user) {
      toast.error("You must be logged in to submit a claim");
      navigate("/auth");
      return;
    }
    setLoading(true);
    try {
      const claimData = {
        user_id: user.id,
        request_type: values.requestType,
        adjuster_name: `${values.adjusterFirstName} ${values.adjusterLastName}`,
        adjuster_email: values.adjusterEmail,
        adjuster_phone: values.phone,
        adjuster_company: values.company,
        adjuster_license: values.ext || "",
        insurance_carrier_name: values.insuranceCarrierName || null,
        claim_number: values.claimNumber,
        loss_date: values.dateOfLoss,
        settlement_date: null,
        settlement_amount: values.aleLimit ? parseFloat(values.aleLimit) : null,
        claim_type: values.typeOfLoss,
        policyholder_name: `${values.policyholderFirstName} ${values.policyholderLastName}`,
        policyholder_email: values.policyholderEmail || "",
        policyholder_phone: values.cellPhone,
        policyholder_address: `${values.streetAddress}${values.unit ? ` Unit ${values.unit}` : ''}`,
        policyholder_city: values.city,
        policyholder_state: values.state,
        policyholder_zip: values.zip,
        family_dynamics: `Adults: ${values.numAdults}, Children: ${values.numKids}, Pets: ${values.pets}${values.squareFootage || values.bedrooms || values.bathrooms ? `, Property: ${values.squareFootage ? values.squareFootage + ' sqft' : ''} ${values.bedrooms ? values.bedrooms + ' bed' : ''} ${values.bathrooms ? values.bathrooms + ' bath' : ''}` : ''}${values.additionalContactFirstName || values.additionalContactLastName ? `, Secondary Contact: ${values.additionalContactFirstName || ''} ${values.additionalContactLastName || ''} ${values.additionalContactCellPhone || ''} ${values.additionalContactEmail || ''}` : ''}`,
        approved_terms: `${values.policyType ? `Policy Type: ${values.policyType}` : ''}${values.leaseLength ? `${values.policyType ? ', ' : ''}Lease: ${values.leaseLength}` : ''}${values.hotelApprovedTo ? `Hotel Approved To: ${format(values.hotelApprovedTo, 'PPP')}` : ''}${values.hotelRooms ? `, Hotel Rooms: ${values.hotelRooms}` : ''}${values.policyTimeLimit ? `, Policy Time Limit: ${values.policyTimeLimit}` : ''}${values.aleAdvanced ? `, ALE Advanced: ${values.aleAdvanced}` : ''}${values.deductibleRequired ? `, Deductible Required: ${values.deductibleRequired}` : ''}${values.deductibleAmount ? `, Deductible Amount: $${values.deductibleAmount}` : ''}`,
        submitted_by: currentUserName || `${values.adjusterFirstName} ${values.adjusterLastName}`.trim() || user?.email || "Unknown",
        submitted_at: new Date().toISOString()
      };
      if (editMode && claimId) {
        // Update existing claim
        const {
          error
        } = await supabase.from("claims").update(claimData).eq("id", claimId);
        if (error) throw error;

        // Log the claim update
        await AdminLogger.claimUpdated(claimId, values.claimNumber);

        toast.success("Claim updated successfully!");
      } else {
        // Insert new claim
        const {
          error
        } = await supabase.from("claims").insert(claimData);
        if (error) throw error;

        // Log the new claim creation
        await AdminLogger.claimCreated(
          values.claimNumber,
          values.typeOfLoss,
          `${values.policyholderFirstName} ${values.policyholderLastName}`,
          `${values.adjusterFirstName} ${values.adjusterLastName}`
        );

        // Notify admins about the new claim
        try {
          await supabase.functions.invoke("notify-new-claim", {
            body: {
              claimNumber: values.claimNumber,
              claimType: values.typeOfLoss,
              policyholderName: `${values.policyholderFirstName} ${values.policyholderLastName}`,
              policyholderEmail: values.policyholderEmail || "",
              policyholderPhone: values.cellPhone,
              policyholderAddress: `${values.streetAddress}${values.unit ? ` Unit ${values.unit}` : ''}, ${values.city}, ${values.state} ${values.zip}`,
              adjusterName: `${values.adjusterFirstName} ${values.adjusterLastName}`,
              adjusterCompany: values.company,
              adjusterEmail: values.adjusterEmail,
              lossDate: values.dateOfLoss,
              familyDynamics: `Adults: ${values.numAdults}, Children: ${values.numKids}, Pets: ${values.pets}`,
              approvedTerms: values.leaseLength || values.hotelApprovedTo ? `${values.leaseLength ? `Lease: ${values.leaseLength}` : ''}${values.hotelApprovedTo ? ` Hotel Approved To: ${format(values.hotelApprovedTo, 'PPP')}` : ''}` : undefined,
            },
          });
          console.log("Admin notification sent successfully");
        } catch (notifyError) {
          console.error("Failed to send admin notification:", notifyError);
          // Don't fail the submission if notification fails
        }

        toast.success("Claim submitted successfully!");
      }
      form.reset();
      navigate("/dashboard");
    } catch (error: any) {
      toast.error(editMode ? "Failed to update claim" : "Failed to submit claim");
      console.error("Error with claim:", error);
    } finally {
      setLoading(false);
    }
  };
  if (authLoading) {
    return <div className="min-h-screen flex flex-col">
      <Navigation />
      <main className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Checking authentication...</p>
        </div>
      </main>
      <Footer />
    </div>;
  }
  return <div className="min-h-screen flex flex-col">
    <Navigation />
    <main className="flex-1 pt-24 pb-6 px-4">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-3xl font-bold text-center mb-2">I want to submit a new placement request</h1>
        <p className="text-center text-muted-foreground mb-8">
          To start a new placement request, please first select the following information.
        </p>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Form Legend */}
            <FormLegend />

            {/* Adjuster Information - Full Width */}
            <div className="space-y-2 pb-3 border-b">
              <h2 className="text-xl font-semibold">Adjuster Information</h2>
              <div className="grid grid-cols-4 gap-3">
                <FormField control={form.control} name="adjusterFirstName" render={({
                  field
                }) => <FormItem>
                    <FormLabel><RequiredFieldIndicator required>First Name</RequiredFieldIndicator></FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
                <FormField control={form.control} name="adjusterLastName" render={({
                  field
                }) => <FormItem>
                    <FormLabel><RequiredFieldIndicator required>Last Name</RequiredFieldIndicator></FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
                <FormField control={form.control} name="company" render={({
                  field
                }) => <FormItem className="col-span-2">
                    <FormLabel><RequiredFieldIndicator required>Company</RequiredFieldIndicator></FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              </div>
              <div className="grid grid-cols-4 gap-3">
                <FormField control={form.control} name="phone" render={({
                  field
                }) => <FormItem>
                    <FormLabel><RequiredFieldIndicator required>Phone</RequiredFieldIndicator></FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
                <FormField control={form.control} name="ext" render={({
                  field
                }) => <FormItem>
                    <FormLabel>Ext.</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
                <FormField control={form.control} name="adjusterEmail" render={({
                  field
                }) => <FormItem className="col-span-2">
                    <FormLabel><RequiredFieldIndicator required>Email Address</RequiredFieldIndicator></FormLabel>
                    <FormControl>
                      <Input type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              </div>
            </div>

            {/* Claim Information */}
            <div className="space-y-2 p-3 rounded-lg bg-[#cbcccd]/[0.23]">
              <h2 className="text-xl font-semibold">Claim Information</h2>
              <FormField control={form.control} name="requestType" render={({
                field
              }) => <FormItem>
                  <FormLabel><RequiredFieldIndicator required className="flex-col items-start gap-0">Placement Request Type</RequiredFieldIndicator></FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose One" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="bg-popover z-50">
                      <SelectItem value="housing">Housing Only</SelectItem>
                      <SelectItem value="housing-and-hotel">Housing and Hotel</SelectItem>
                      <SelectItem value="hotel-only">Hotel Only</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />

              {/* Approved Terms - Hidden for FRV Only */}
              {form.watch("requestType") !== "frv-only" && <div className="space-y-2 pt-2">
                <div className="grid md:grid-cols-2 gap-2">
                  {form.watch("requestType") === "hotel-only" ? <>
                    <FormField control={form.control} name="hotelApprovedTo" render={({
                      field
                    }) => <FormItem>
                        <FormLabel><RequiredFieldIndicator required>Hotel Booking Length</RequiredFieldIndicator></FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button variant={"outline"} className={cn("w-full pl-3 text-left font-normal", !field.value && "text-muted-foreground")}>
                                {field.value && !isNaN(new Date(field.value).getTime()) ? format(field.value, "PPP") : <span>Pick a date</span>}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 bg-popover z-50" align="start">
                            <Calendar mode="single" selected={field.value} onSelect={field.onChange} disabled={date => date < new Date()} initialFocus className={cn("p-3 pointer-events-auto")} />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>} />
                    <FormField control={form.control} name="hotelRooms" render={({
                      field
                    }) => <FormItem>
                        <FormLabel>Number of Hotel Rooms</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-popover z-50">
                            {[1, 2, 3, 4, 5].map(num => <SelectItem key={num} value={num.toString()}>
                              {num}
                            </SelectItem>)}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>} />
                  </> : <>
                    <FormField control={form.control} name="leaseLength" render={({
                      field
                    }) => <FormItem>
                        <FormLabel><RequiredFieldIndicator required>Housing Lease Length</RequiredFieldIndicator></FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Unknown" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-popover z-50">
                            <SelectItem value="month-to-month">Month to Month</SelectItem>
                            <SelectItem value="1-month">1 Month</SelectItem>
                            <SelectItem value="2-months">2 Months</SelectItem>
                            <SelectItem value="3-months">3 Months</SelectItem>
                            <SelectItem value="4-months">4 Months</SelectItem>
                            <SelectItem value="5-months">5 Months</SelectItem>
                            <SelectItem value="6-months">6 Months</SelectItem>
                            <SelectItem value="7-months">7 Months</SelectItem>
                            <SelectItem value="8-months">8 Months</SelectItem>
                            <SelectItem value="10-months">10 Months</SelectItem>
                            <SelectItem value="11-months">11 Months</SelectItem>
                            <SelectItem value="12-months">12 Months</SelectItem>
                            <SelectItem value="unknown">Unknown</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>} />
                    {form.watch("requestType") === "housing-and-hotel" && <div className="space-y-2">
                      <FormField control={form.control} name="hotelApprovedTo" render={({
                        field
                      }) => <FormItem>
                          <FormLabel>Hotel Booking Length</FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button variant={"outline"} className={cn("w-full pl-3 text-left font-normal", !field.value && "text-muted-foreground")}>
                                  {field.value && !isNaN(new Date(field.value).getTime()) ? format(field.value, "PPP") : <span>Pick a date</span>}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 bg-popover z-50" align="start">
                              <Calendar mode="single" selected={field.value} onSelect={field.onChange} disabled={date => date < new Date()} initialFocus className={cn("p-3 pointer-events-auto")} />
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>} />
                      <FormField control={form.control} name="hotelRooms" render={({
                        field
                      }) => <FormItem>
                          <FormLabel>Number of Hotel Rooms</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="bg-popover z-50">
                              {[1, 2, 3, 4, 5].map(num => <SelectItem key={num} value={num.toString()}>
                                {num}
                              </SelectItem>)}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>} />
                    </div>}
                  </>}
                </div>
              </div>}

              <div className="grid grid-cols-4 gap-2 pt-2 border-t">
                <FormField control={form.control} name="insuranceCarrierName" render={({
                  field
                }) => <FormItem>
                    <FormLabel><RequiredFieldIndicator required>Insurance Carrier Name</RequiredFieldIndicator></FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
                <FormField control={form.control} name="claimNumber" render={({
                  field
                }) => <FormItem>
                    <FormLabel><RequiredFieldIndicator required>Claim Number</RequiredFieldIndicator></FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
                <FormField control={form.control} name="dateOfLoss" render={({
                  field
                }) => <FormItem>
                    <FormLabel><RequiredFieldIndicator required>Date of Loss</RequiredFieldIndicator></FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              </div>
              <div className="grid grid-cols-4 gap-3 pb-3 border-b">
                <FormField control={form.control} name="typeOfLoss" render={({
                  field
                }) => <FormItem>
                    <FormLabel><RequiredFieldIndicator required className="flex-col items-start gap-0">Loss Type</RequiredFieldIndicator></FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose One" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-popover z-50">
                        <SelectItem value="fire">Fire</SelectItem>
                        <SelectItem value="water">Water</SelectItem>
                        <SelectItem value="storm">Storm</SelectItem>
                        <SelectItem value="flood">Flood</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>} />
                {form.watch("typeOfLoss") === "other" && <FormField control={form.control} name="otherExplanation" render={({
                  field
                }) => <FormItem>
                    <FormLabel>Other Explanation</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Please explain" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />}
                <FormField control={form.control} name="policyType" render={({
                  field
                }) => <FormItem>
                    <FormLabel><RequiredFieldIndicator required>Policy Type</RequiredFieldIndicator></FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose One" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="homeowners">Homeowners</SelectItem>
                        <SelectItem value="renters">Renters</SelectItem>
                        <SelectItem value="commercial">Commercial</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>} />

                {/* Deductible Fields - Only show for Renters Insurance */}
                {form.watch("policyType") === "renters" && <>
                  <FormField control={form.control} name="deductibleRequired" render={({
                    field
                  }) => <FormItem>
                      <FormLabel>Deductible Required</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Choose One" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>} />
                  {form.watch("deductibleRequired") === "yes" && <FormField control={form.control} name="deductibleAmount" render={({
                    field
                  }) => <FormItem>
                      <FormLabel>Deductible Amount</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder="0.00" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />}
                </>}
              </div>

              <div className="grid grid-cols-4 gap-3">
                <FormField control={form.control} name="aleLimit" render={({
                  field
                }) => <FormItem>
                    <FormLabel>ALE Limit</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input
                          type="text"
                          placeholder=""
                          className="pl-7"
                          value={field.value || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "" || /^\d*\.?\d*$/.test(value)) {
                              field.onChange(value);
                            }
                          }}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
                <FormField control={form.control} name="policyTimeLimit" render={({
                  field
                }) => <FormItem>
                    <FormLabel>Policy Time Limit</FormLabel>
                    <Select onValueChange={(value) => field.onChange(value === "none" ? "" : value)} value={field.value || "none"}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose One" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-popover z-50">
                        <SelectItem value="none">--</SelectItem>
                        <SelectItem value="12-months">12 months</SelectItem>
                        <SelectItem value="24-months">24 months</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>} />
              </div>
              <div className="grid grid-cols-4 gap-3">
                <FormField control={form.control} name="aleAdvanced" render={({
                  field
                }) => <FormItem>
                    <FormLabel>ALE Advanced</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input type="number" placeholder="0.00" className="pl-7" {...field} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              </div>

              <div className="grid grid-cols-4 gap-3 pt-2 border-t">
                <FormField control={form.control} name="frvRequested" render={({
                  field
                }) => <FormItem>
                    <FormLabel>FRV Requested</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose One" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-popover z-50">
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>} />
              </div>

              {/* Document Upload */}
              <div className="space-y-2 pt-2 border-t">
                <Label>Document Upload</Label>
                <div onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave} className={cn("flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed rounded-lg cursor-pointer transition-colors", isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/30 hover:bg-muted/50")}>
                  <Upload className={cn("h-8 w-8", isDragging ? "text-primary" : "text-muted-foreground")} />
                  <div className="text-center">
                    <p className={cn("text-sm font-medium", isDragging ? "text-primary" : "text-muted-foreground")}>
                      {isDragging ? "Drop files here" : "Drag & drop files here"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">or</p>
                  </div>
                  <label className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium cursor-pointer hover:bg-primary/90 transition-colors">
                    Browse Files
                    <input type="file" multiple onChange={handleFileUpload} className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" />
                  </label>
                  <p className="text-xs text-muted-foreground">PDF, DOC, DOCX, JPG, PNG</p>
                </div>
                {uploadedFiles.length > 0 && <div className="flex flex-wrap gap-2 mt-2">
                  {uploadedFiles.map((file, index) => <div key={index} className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-md text-sm">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="max-w-[150px] truncate">{file.name}</span>
                    <button type="button" onClick={() => removeFile(index)} className="text-muted-foreground hover:text-foreground">
                      <X className="h-4 w-4" />
                    </button>
                  </div>)}
                </div>}
              </div>
            </div>

            <div className="space-y-2 p-3 rounded-lg bg-[#cbcccd]/[0.23]">
              <h2 className="text-xl font-semibold">Policyholder Information</h2>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Primary Contact */}
                <div className="space-y-2">
                  <h3 className="text-lg font-medium">Primary Contact</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField control={form.control} name="policyholderFirstName" render={({
                      field
                    }) => <FormItem>
                        <FormLabel><RequiredFieldIndicator required>First Name</RequiredFieldIndicator></FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                    <FormField control={form.control} name="policyholderLastName" render={({
                      field
                    }) => <FormItem>
                        <FormLabel><RequiredFieldIndicator required>Last Name</RequiredFieldIndicator></FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField control={form.control} name="cellPhone" render={({
                      field
                    }) => <FormItem>
                        <FormLabel><RequiredFieldIndicator required>Cell Phone</RequiredFieldIndicator></FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                    <FormField control={form.control} name="policyholderEmail" render={({
                      field
                    }) => <FormItem>
                        <FormLabel>Email Address</FormLabel>
                        <FormControl>
                          <Input type="email" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                  </div>
                </div>

                {/* Secondary Contact */}
                <div className="space-y-2">
                  <h3 className="text-lg font-medium">Secondary Contact</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField control={form.control} name="additionalContactFirstName" render={({
                      field
                    }) => <FormItem>
                        <FormLabel>First Name</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                    <FormField control={form.control} name="additionalContactLastName" render={({
                      field
                    }) => <FormItem>
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField control={form.control} name="additionalContactCellPhone" render={({
                      field
                    }) => <FormItem>
                        <FormLabel>Cell Phone</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                    <FormField control={form.control} name="additionalContactEmail" render={({
                      field
                    }) => <FormItem>
                        <FormLabel>Email Address</FormLabel>
                        <FormControl>
                          <Input type="email" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                  </div>
                </div>
              </div>

              {/* Full-width separator */}
              <div className="border-b"></div>

              {/* Loss Address and Family Dynamics side by side */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Loss Address */}
                <div className="space-y-2">
                  <h3 className="text-lg font-medium">Loss Address</h3>
                  <div className="grid grid-cols-3 gap-3">
                    <FormField control={form.control} name="streetAddress" render={({
                      field
                    }) => <FormItem className="col-span-2">
                        <FormLabel><RequiredFieldIndicator required>Street Address</RequiredFieldIndicator></FormLabel>
                        <FormControl>
                          <AddressAutocomplete
                            value={field.value || ""}
                            onChange={field.onChange}
                            onPlaceSelected={({ street, city, state, zip }) => {
                              if (street) form.setValue("streetAddress", street, { shouldValidate: true });
                              if (city) form.setValue("city", city, { shouldValidate: true });
                              if (state) form.setValue("state", state, { shouldValidate: true });
                              if (zip) form.setValue("zip", zip, { shouldValidate: true });
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                    <FormField control={form.control} name="unit" render={({
                      field
                    }) => <FormItem>
                        <FormLabel>Unit</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <FormField control={form.control} name="city" render={({
                      field
                    }) => <FormItem>
                        <FormLabel><RequiredFieldIndicator required>City</RequiredFieldIndicator></FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                    <FormField control={form.control} name="state" render={({
                      field
                    }) => <FormItem>
                        <FormLabel><RequiredFieldIndicator required>State</RequiredFieldIndicator></FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Choose One" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-popover z-50">
                            <SelectItem value="AL">Alabama</SelectItem>
                            <SelectItem value="AK">Alaska</SelectItem>
                            <SelectItem value="AZ">Arizona</SelectItem>
                            <SelectItem value="AR">Arkansas</SelectItem>
                            <SelectItem value="CA">California</SelectItem>
                            <SelectItem value="CO">Colorado</SelectItem>
                            <SelectItem value="CT">Connecticut</SelectItem>
                            <SelectItem value="DE">Delaware</SelectItem>
                            <SelectItem value="FL">Florida</SelectItem>
                            <SelectItem value="GA">Georgia</SelectItem>
                            <SelectItem value="HI">Hawaii</SelectItem>
                            <SelectItem value="ID">Idaho</SelectItem>
                            <SelectItem value="IL">Illinois</SelectItem>
                            <SelectItem value="IN">Indiana</SelectItem>
                            <SelectItem value="IA">Iowa</SelectItem>
                            <SelectItem value="KS">Kansas</SelectItem>
                            <SelectItem value="KY">Kentucky</SelectItem>
                            <SelectItem value="LA">Louisiana</SelectItem>
                            <SelectItem value="ME">Maine</SelectItem>
                            <SelectItem value="MD">Maryland</SelectItem>
                            <SelectItem value="MA">Massachusetts</SelectItem>
                            <SelectItem value="MI">Michigan</SelectItem>
                            <SelectItem value="MN">Minnesota</SelectItem>
                            <SelectItem value="MS">Mississippi</SelectItem>
                            <SelectItem value="MO">Missouri</SelectItem>
                            <SelectItem value="MT">Montana</SelectItem>
                            <SelectItem value="NE">Nebraska</SelectItem>
                            <SelectItem value="NV">Nevada</SelectItem>
                            <SelectItem value="NH">New Hampshire</SelectItem>
                            <SelectItem value="NJ">New Jersey</SelectItem>
                            <SelectItem value="NM">New Mexico</SelectItem>
                            <SelectItem value="NY">New York</SelectItem>
                            <SelectItem value="NC">North Carolina</SelectItem>
                            <SelectItem value="ND">North Dakota</SelectItem>
                            <SelectItem value="OH">Ohio</SelectItem>
                            <SelectItem value="OK">Oklahoma</SelectItem>
                            <SelectItem value="OR">Oregon</SelectItem>
                            <SelectItem value="PA">Pennsylvania</SelectItem>
                            <SelectItem value="RI">Rhode Island</SelectItem>
                            <SelectItem value="SC">South Carolina</SelectItem>
                            <SelectItem value="SD">South Dakota</SelectItem>
                            <SelectItem value="TN">Tennessee</SelectItem>
                            <SelectItem value="TX">Texas</SelectItem>
                            <SelectItem value="UT">Utah</SelectItem>
                            <SelectItem value="VT">Vermont</SelectItem>
                            <SelectItem value="VA">Virginia</SelectItem>
                            <SelectItem value="WA">Washington</SelectItem>
                            <SelectItem value="WV">West Virginia</SelectItem>
                            <SelectItem value="WI">Wisconsin</SelectItem>
                            <SelectItem value="WY">Wyoming</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>} />
                    <FormField control={form.control} name="zip" render={({
                      field
                    }) => <FormItem>
                        <FormLabel><RequiredFieldIndicator required>Zip</RequiredFieldIndicator></FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <FormField control={form.control} name="squareFootage" render={({
                      field
                    }) => <FormItem>
                        <FormLabel>Square Footage</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                    <FormField control={form.control} name="bedrooms" render={({
                      field
                    }) => <FormItem>
                        <FormLabel>Bedrooms</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                    <FormField control={form.control} name="bathrooms" render={({
                      field
                    }) => <FormItem>
                        <FormLabel>Bathrooms</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                  </div>
                </div>

                {/* Family Dynamics */}
                <div className="space-y-2">
                  <h3 className="text-lg font-medium">Family Dynamics</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField control={form.control} name="numAdults" render={({
                      field
                    }) => <FormItem>
                        <FormLabel><RequiredFieldIndicator required># of Adults</RequiredFieldIndicator></FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {[1, 2, 3, 4, 5, 6].map(num => <SelectItem key={num} value={num.toString()}>
                              {num}
                            </SelectItem>)}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>} />
                    <FormField control={form.control} name="numKids" render={({
                      field
                    }) => <FormItem>
                        <FormLabel><RequiredFieldIndicator required># of Children</RequiredFieldIndicator></FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {[0, 1, 2, 3, 4, 5, 6].map(num => <SelectItem key={num} value={num.toString()}>
                              {num}
                            </SelectItem>)}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField control={form.control} name="pets" render={({
                      field
                    }) => <FormItem>
                        <FormLabel>Pets</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Unknown" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="yes">Yes</SelectItem>
                            <SelectItem value="no">No</SelectItem>
                            <SelectItem value="unknown">Unknown</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>} />
                    <FormField control={form.control} name="numPets" render={({
                      field
                    }) => <FormItem>
                        <FormLabel># of Pets</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-popover z-50">
                            {[0, 1, 2, 3, 4, 5].map(num => <SelectItem key={num} value={num.toString()}>
                              {num}
                            </SelectItem>)}
                            <SelectItem value="unknown">Unknown</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>} />
                  </div>
                  <FormField control={form.control} name="petDescription" render={({
                    field
                  }) => <FormItem>
                      <FormLabel>Pet(s) Description</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Description of Pets" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
                </div>
              </div>
            </div>

            {/* Special Circumstances */}
            <FormField control={form.control} name="specialCircumstances" render={({
              field
            }) => <FormItem>
                <FormLabel>
                  Are there any special circumstances or accommodations we should know about?
                </FormLabel>
                <FormControl>
                  <Textarea className="min-h-[120px]" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

            <div className="flex flex-col md:flex-row gap-3">
              <Button type="submit" size="lg" className="w-full md:w-auto" disabled={loading}>
                {loading ? editMode ? "Updating..." : "Submitting..." : editMode ? "Update Placement Request" : "Submit Placement Request"}
              </Button>

              {editMode && claimId && (
                <AlertDialog open={closeDialogOpen} onOpenChange={setCloseDialogOpen}>
                  <AlertDialogTrigger asChild>
                    <Button
                      type="button"
                      variant="destructive"
                      size="lg"
                      className="w-full md:w-auto"
                      disabled={loading || closingClaim}
                    >
                      <XCircle className="mr-2 h-4 w-4" />
                      Close Claim
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Request to Close Claim</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will notify TPS Housing via email that you are requesting to close this claim.
                        Optionally add a reason or notes below.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <Textarea
                      placeholder="Reason for closing (optional)"
                      value={closeReason}
                      onChange={(e) => setCloseReason(e.target.value)}
                      className="min-h-[100px]"
                      disabled={closingClaim}
                    />
                    <AlertDialogFooter>
                      <AlertDialogCancel disabled={closingClaim}>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={(e) => { e.preventDefault(); handleCloseClaim(); }}
                        disabled={closingClaim}
                      >
                        {closingClaim ? "Sending..." : "Send Close Request"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </form>
        </Form>
      </div>
    </main>
    <Footer />
  </div>;
};
export default Claims;
